import React, { useState } from "react";
import { Check, ShoppingBag } from "lucide-react";
import { placeOrder, OrderData } from "../utils/share";
import { useStoreInfo } from "../contexts/StoreInfoContext";
import { Product } from "../types";

interface ShareButtonProps {
  product: {
    productName: string;
    price: number;
    productUrl?: string;
  };
  fullProduct?: Product; // Full product details for order placement
  className?: string;
}

export const ShareButton: React.FC<ShareButtonProps> = ({
  product,
  fullProduct,
  className = "",
}) => {
  const [isOrdered, setIsOrdered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const storeInfo = useStoreInfo();
  const phoneNumber = storeInfo.phone || storeInfo.phone2 || "";

  const handleOrder = async () => {
    if (!phoneNumber) {
      alert(
        "Store phone number not available. Please contact the store directly."
      );
      return;
    }

    setIsLoading(true);
    try {
      // Prepare order data
      const orderData: OrderData = {
        productId: fullProduct?.id || "",
        productName: product.productName,
        price: product.price,
        originalPrice: fullProduct?.originalPrice,
        quantity: 1, // Default quantity, can be enhanced later
        productUrl:
          product.productUrl ||
          `${window.location.origin}/product/${fullProduct?.id}`,
        image: fullProduct?.image,
      };

      // Place order (stores locally and redirects to WhatsApp)
      const success = await placeOrder(orderData, phoneNumber);

      if (success) {
        setIsOrdered(true);
        setTimeout(() => setIsOrdered(false), 3000);
      } else {
        alert("Failed to place order. Please try again.");
      }
    } catch (error) {
      console.error("Order failed:", error);
      alert("Failed to place order. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleOrder}
      disabled={isLoading || !phoneNumber}
      className={`w-full flex items-center justify-center py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 ${
        isOrdered
          ? "bg-green-500 text-white"
          : isLoading || !phoneNumber
          ? "bg-gray-400 text-gray-200 cursor-not-allowed"
          : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-xl hover:shadow-2xl transform hover:scale-110 hover-bounce border-2 border-white/50"
      } ${className}`}
    >
      {isOrdered ? (
        <>
          <Check className="h-5 w-5 mr-2" />
          Order Placed! 🎉
        </>
      ) : isLoading ? (
        <>
          <span className="text-sm mr-2 animate-bounce">📤</span>
          Placing Order...
        </>
      ) : (
        <>
          <ShoppingBag className="h-5 w-5 mr-2" />
          Order via WhatsApp
        </>
      )}
    </button>
  );
};

export default ShareButton;
